import { ConversationActorsType } from '@shared/services/conversationApiService/apiService';
import { CallActors } from '@shared/constants/recipient-status.constants';
import { ExtendedConversationLogDto } from './types';

export interface LiveTranscriptionSegment {
  id?: string;
  text: string;
  firstReceivedTime: number;
  lastReceivedTime?: number;
  participantType?: 'agent' | 'caller' | 'monitor';
}

const PERSISTED_MATCH_WINDOW_MS = 30_000;

const normalizeTranscriptText = (text: string) =>
  text
    .toLowerCase()
    .replace(/[\s.,!?;:"'`()[\]{}«»“”‘’…\-–—/\\*_~]+/g, ' ')
    .trim();

const persistedActorsFor = (
  participantType: LiveTranscriptionSegment['participantType'],
): ConversationActorsType[] => {
  if (participantType === 'agent') return [CallActors.AGENT];
  if (participantType === 'monitor') return [CallActors.SUPERVISOR, CallActors.CUSTOMER];
  return [CallActors.CUSTOMER];
};

const isPersisted = (
  segment: LiveTranscriptionSegment,
  normalizedText: string,
  persisted: ExtendedConversationLogDto[],
) => {
  const actors = persistedActorsFor(segment.participantType);
  const windowStart = segment.firstReceivedTime - PERSISTED_MATCH_WINDOW_MS;
  const windowEnd =
    (segment.lastReceivedTime ?? segment.firstReceivedTime) + PERSISTED_MATCH_WINDOW_MS;

  return persisted.some((message) => {
    if (message.type === 'tool_call' || !actors.includes(message.actor)) return false;
    const createdAt = new Date(message.createdAt).getTime();
    if (createdAt < windowStart || createdAt > windowEnd) return false;
    return normalizeTranscriptText(message.message).includes(normalizedText);
  });
};

export const mergeLiveTranscriptions = (
  persisted: ExtendedConversationLogDto[],
  segments: LiveTranscriptionSegment[],
  conversationId: string,
): ExtendedConversationLogDto[] => {
  const live = segments.flatMap((segment, index): ExtendedConversationLogDto[] => {
    const normalizedText = normalizeTranscriptText(segment.text);
    if (!normalizedText || isPersisted(segment, normalizedText, persisted)) return [];

    const actor =
      segment.participantType === 'agent' ? CallActors.AGENT : CallActors.CUSTOMER;
    const receivedAt = new Date(segment.firstReceivedTime).toISOString();

    return [
      {
        id: `live-${segment.id || index}-${segment.firstReceivedTime}`,
        conversationId,
        actor,
        message: segment.text,
        type: 'text',
        createdAt: receivedAt,
        updatedAt: receivedAt,
        isMonitor: segment.participantType === 'monitor',
        isLive: true,
      },
    ];
  });

  return [...persisted, ...live];
};
